/** Versioned, typed client/server protocol. Every incoming client message is
 * validated with these Zod schemas before it is processed (CLAUDE.md rule 5). */
import { z } from 'zod';
import type { Notice, ProfileData } from '@aleaces/engine';

export const PROTOCOL_VERSION = 2;

// ---------- shared ----------
export const CardSchema = z.object({ r: z.number().int().min(2).max(14), s: z.number().int().min(0).max(3) });
export type Card = z.infer<typeof CardSchema>;

export const GameModeSchema = z.enum(['ranked', 'casual']);
export type GameMode = z.infer<typeof GameModeSchema>;

export const PlayerActionSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('fold') }),
  z.object({ type: z.literal('check') }),
  z.object({ type: z.literal('call') }),
  z.object({ type: z.literal('raise'), to: z.number().int().positive().max(1_000_000) }),
]);
export type PlayerAction = z.infer<typeof PlayerActionSchema>;

// ---------- client -> server ----------
export const ClientHelloSchema = z.object({
  type: z.literal('hello'),
  v: z.literal(PROTOCOL_VERSION),
  /** account token from an earlier session; null/absent creates a new account */
  token: z.string().min(8).max(128).nullish(),
  /** display name used only when a new account is created */
  name: z.string().min(1).max(24).optional(),
  /** Supabase access token (JWT); when present and valid, the account is keyed
   * by the Supabase user id and the anonymous token only links old progress */
  supabaseToken: z.string().min(16).max(4096).optional(),
});

export const ClientEquipSchema = z.object({
  type: z.literal('equip'),
  slot: z.enum(['cardback', 'felt', 'frame', 'title']),
  id: z.string().min(1).max(32),
});
export const ClientEquipSkinSchema = z.object({
  type: z.literal('equipSkin'),
  hero: z.string().min(1).max(32),
  skin: z.string().min(1).max(32),
});
export const ClientFavoriteHeroSchema = z.object({ type: z.literal('setFavoriteHero'), hero: z.string().min(1).max(32) });
export const ClientSetNameSchema = z.object({ type: z.literal('setName'), name: z.string().min(1).max(24) });
export const ClientBuyPremiumSchema = z.object({ type: z.literal('buyPremium') });
export const ClientRedeemCodeSchema = z.object({ type: z.literal('redeemCode'), code: z.string().min(3).max(32) });

export const ClientQueueSchema = z.object({ type: z.literal('queue'), mode: GameModeSchema });
export const ClientCancelQueueSchema = z.object({ type: z.literal('cancelQueue') });
export const ClientRerollSchema = z.object({ type: z.literal('reroll') });
export const ClientReadySchema = z.object({ type: z.literal('ready') });
export const ClientActSchema = z.object({ type: z.literal('act'), action: PlayerActionSchema });
export const ClientAbilitySchema = z.object({ type: z.literal('useAbility') });
export const ClientEmoteSchema = z.object({ type: z.literal('emote'), text: z.string().min(1).max(80) });
export const ClientLeaveSchema = z.object({ type: z.literal('leaveTable') });
export const ReportReasonSchema = z.enum(['abuse', 'collusion', 'bot', 'other']);
export type ReportReason = z.infer<typeof ReportReasonSchema>;
export const ClientReportSchema = z.object({
  type: z.literal('report'),
  targetSeat: z.number().int().min(0).max(9),
  reason: ReportReasonSchema,
  comment: z.string().max(200).optional(),
});

export const ClientMessageSchema = z.discriminatedUnion('type', [
  ClientHelloSchema,
  ClientQueueSchema,
  ClientCancelQueueSchema,
  ClientRerollSchema,
  ClientReadySchema,
  ClientActSchema,
  ClientAbilitySchema,
  ClientEmoteSchema,
  ClientLeaveSchema,
  ClientEquipSchema,
  ClientEquipSkinSchema,
  ClientFavoriteHeroSchema,
  ClientSetNameSchema,
  ClientBuyPremiumSchema,
  ClientRedeemCodeSchema,
  ClientReportSchema,
]);
export type ClientMessage = z.infer<typeof ClientMessageSchema>;

// ---------- server -> client ----------
export interface SeatView {
  seat: number;
  id: string;
  name: string;
  heroId: string;
  skinId: string;
  frame: string;
  title: string;
  isBot: boolean;
  connected: boolean;
  chips: number;
  roundBet: number;
  totalBet: number;
  inHand: boolean;
  allIn: boolean;
  out: boolean;
  cardCount: number;
  /** only present when publicly revealed (showdown / all-in run-out) */
  revealed?: Card[];
}

export interface TableSnapshot {
  tableId: string;
  mode: GameMode;
  handNo: number;
  level: number;
  blinds: [number, number];
  buttonSeat: number;
  street: number;
  board: Card[];
  potTotal: number;
  currentBet: number;
  minRaise: number;
  toAct: number | null;
  /** epoch ms until the acting player is auto-folded */
  actDeadline: number | null;
  players: SeatView[];
  you: {
    seat: number;
    hole: Card[];
    abilityUsed: boolean;
    abilityActive: boolean;
    legal: { toCall: number; minTo: number; maxTo: number; canRaise: boolean; canCheck: boolean } | null;
  } | null;
}

export type ClientEvent =
  | { type: 'handStart'; handNo: number; level: number; sb: number; bb: number; buttonSeat: number; sbSeat: number; bbSeat: number }
  | { type: 'blind'; seat: number; amount: number; allIn: boolean }
  | { type: 'deal'; hole: Card[] }
  | { type: 'turn'; seat: number; toCall: number; deadline: number }
  | { type: 'action'; seat: number; kind: 'fold' | 'check' | 'call' | 'bet' | 'raise' | 'allin'; amount: number; to: number }
  | { type: 'street'; street: number; cards: Card[] }
  | { type: 'reveal'; hands: { seat: number; cards: Card[] }[] }
  | { type: 'roar'; seat: number }
  | { type: 'ability'; seat: number; abilityId: string }
  | { type: 'peek'; targetSeat: number; cardIndex: number; card: Card }
  | { type: 'abilityPayout'; seat: number; abilityId: string; amount: number }
  | { type: 'shield'; seat: number; chips: number }
  | { type: 'showdown'; reveals: { seat: number; cards: Card[] }[]; winners: { seat: number; amount: number; handName: string; cards: Card[] }[]; potTotal: number; split: boolean }
  | { type: 'winWithoutShowdown'; seat: number; amount: number }
  | { type: 'handEnd'; handNo: number; you: { won: number; wentShowdown: boolean; wonShowdown: boolean; wasAllIn: boolean; stolen: boolean; score: number } | null }
  | { type: 'eliminated'; seat: number; placement: number }
  | { type: 'emote'; seat: number; text: string };

export interface MatchResult {
  mode: GameMode;
  placement: number;
  tableSize: number;
  xp: number;
  taler: number;
  ratingDelta: number;
  newRating: number;
}

export type ServerMessage =
  | { type: 'welcome'; playerId: string; v: number }
  | {
      type: 'profile';
      token: string;
      profile: ProfileData;
      notices: Notice[];
      /** server-authoritative epoch ms when the daily quests reset (next server midnight) */
      questResetAt: number;
      /** server-authoritative epoch ms when the weekly quests reset (next server Monday 0:00) */
      weeklyResetAt: number;
    }
  | { type: 'queueUpdate'; mode: GameMode; inQueue: number; needed: number; secondsLeft: number }
  | { type: 'queueCancelled' }
  | { type: 'matchFound'; tableId: string; mode: GameMode; heroId: string; rerollsLeft: number; chooseSeconds: number; players: { name: string }[] }
  | { type: 'heroUpdate'; heroId: string; rerollsLeft: number }
  | { type: 'matchStart'; snapshot: TableSnapshot }
  | { type: 'state'; snapshot: TableSnapshot }
  | { type: 'events'; events: ClientEvent[] }
  | { type: 'result'; result: MatchResult }
  | { type: 'tableClosed' }
  | { type: 'error'; message: string };

export function parseClientMessage(raw: unknown): ClientMessage | null {
  if (typeof raw !== 'string') return null;
  let json: unknown;
  try {
    json = JSON.parse(raw);
  } catch {
    return null;
  }
  const res = ClientMessageSchema.safeParse(json);
  return res.success ? res.data : null;
}
